import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Instagram, Clapperboard, Mail, MessageCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { CONTACT } from "@/constants/aero";
import { SOCIALS } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LINKS = [
  { label: "Home", hash: "#home" },
  { label: "Films", hash: "#filmography" },
  { label: "Services", route: "/services" },
  { label: "Film School", route: "/film-school" },
  { label: "Contact", hash: "#contact" },
];

const SERVICE_LINKS = ["Drone Shoots", "Wedding Films", "Commercials", "Documentaries", "Real Estate"];

const POLICY_LINKS = [
  { label: "Terms & Conditions", slug: "terms-and-conditions" },
  { label: "Privacy Policy", slug: "privacy-policy" },
  { label: "Shipping & Delivery", slug: "shipping-delivery-policy" },
  { label: "Refund Policy", slug: "refund-policy" },
  { label: "Disclaimer", slug: "disclaimer" },
  { label: "Copyright Policy", slug: "copyright-policy" },
];

const linkCls = "font-mono text-[14px] font-medium tracking-[0.1em] uppercase text-[#8FA3B8] hover:text-white transition-colors duration-300";

export const AeroFooter = () => {
  const { pathname } = useLocation();
  const base = pathname === "/" ? "" : "/";
  const linkHref = (l) => l.route || `${base}${l.hash}`;
  const [waHref, setWaHref] = useState(SOCIALS.instagram);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) setWaHref(`https://wa.me/${r.data.whatsapp_number}`);
      })
      .catch(() => {});
  }, []);

  const CONNECT = [
    { label: "Instagram", icon: Instagram, href: SOCIALS.instagram },
    { label: "IMDb", icon: Clapperboard, href: CONTACT.imdb },
    { label: "Email", icon: Mail, href: `mailto:${CONTACT.email}` },
    { label: "WhatsApp", icon: MessageCircle, href: waHref },
  ];

  return (
    <footer data-testid="aero-footer" className="bg-[#0F2A43] text-white">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-[1320px] mx-auto px-6 md:px-10 py-16 md:py-20 grid sm:grid-cols-2 lg:grid-cols-12 gap-12"
      >
        {/* Brand */}
        <div className="lg:col-span-3">
          <a href={pathname === "/" ? "#home" : "/"} data-testid="aero-footer-logo" className="leading-none inline-block">
            <img src="/assets/logo-white.png" alt="FilmmakerAkki — Akhilesh Kumar Suman" className="h-16 w-auto" />
          </a>
          <p className="mt-6 text-[15px] text-[#8FA3B8] leading-relaxed max-w-xs">
            Aerial cinematography for films, OTT, documentaries, and brands. Stories. People. Places. From above.
          </p>
        </div>

        {/* Navigation */}
        <div className="lg:col-span-2">
          <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-white/90">Navigation</p>
          <ul className="mt-6 space-y-3.5">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <a data-testid={`aero-footer-${l.label.toLowerCase().replace(/\s+/g, "-")}`} href={linkHref(l)} className={linkCls}>
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="lg:col-span-2">
          <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-white/90">Services</p>
          <ul className="mt-6 space-y-3.5">
            {SERVICE_LINKS.map((s) => (
              <li key={s}>
                <a href="/services" className={linkCls}>{s}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Policies */}
        <div className="lg:col-span-3">
          <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-white/90">Policies</p>
          <ul className="mt-6 space-y-3.5">
            {POLICY_LINKS.map((p) => (
              <li key={p.slug}>
                <a data-testid={`aero-footer-policy-${p.slug}`} href={`/policies/${p.slug}`} className={linkCls}>
                  {p.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Connect */}
        <div className="lg:col-span-2">
          <p className="font-mono text-[13px] tracking-[0.28em] uppercase text-white/90">Connect</p>
          <ul className="mt-6 space-y-3.5">
            {CONNECT.map((c) => {
              const Icon = c.icon;
              return (
                <li key={c.label}>
                  <a
                    data-testid={`aero-footer-connect-${c.label.toLowerCase()}`}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3"
                  >
                    <Icon size={14} strokeWidth={1.4} className="text-[#8FA3B8] group-hover:text-white transition-colors duration-300" />
                    <span className={linkCls}>{c.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>
          <p className="mt-8 font-mono text-[12px] tracking-[0.2em] uppercase text-[#8FA3B8]">
            {CONTACT.phone} · India
          </p>
        </div>
      </motion.div>

      {/* Bottom strip */}
      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[12px] tracking-[0.2em] uppercase text-[#8FA3B8]">
            © 2026 FILMMAKERAKKI — Crafted with cinematic precision.
          </p>
          <a
            data-testid="aero-footer-imdb"
            href={CONTACT.imdb}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[12px] tracking-[0.22em] uppercase text-[#8FA3B8] hover:text-white transition-colors duration-300"
          >
            IMDb — Akhilesh Kumar Suman
          </a>
        </div>
      </div>
    </footer>
  );
};
